import { type Client, type Sink, createClient, type ClientOptions } from 'graphql-ws';
import { type RequestParameters, type Variables, type CacheConfig } from 'relay-runtime';
import { type FormattedExecutionResult } from 'graphql';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

import { getInternetConnectionState } from './getInternetConnectionState.ts';
import { getBackOffDelay, getDelayRangeFromGraphqlWebsocketState } from './getBackOffDelay.ts';
import { calculateBackOffRange } from './calculateBackOffRange.ts';
import {
	type AGGError,
	captureErrors,
	startCaptureGraphQlErrors,
	stopCaptureGraphQlErrors,
} from '@atlassian/relay-errors';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

export const KEEP_ALIVE = 600000;

export class RelayWebSocketSubscriptionHandler {
	public client: Client;
	private internetConnectionState: 'healthy' | 'unhealthy' | null = 'healthy';
	private timedOut: ReturnType<typeof setTimeout> | null = null;

	private closedInfo: CloseEvent | null = null;
	private replayQueries: Record<string, { unsubscribe: () => void; query: string }> = {};

	private currentGetInternetConnectionStatePromise: ReturnType<
		typeof getInternetConnectionState
	> | null = null;

	constructor(options: ClientOptions) {
		this.client = createClient({
			...options,
			retryWait: this.retryWait,
			on: {
				connected: this.onConnected,
				closed: (info: any) => {
					this.closedInfo = info;
				},
				ping: (received) => {
					if (!received /* sent */) {
						this.timedOut = setTimeout(() => {
							// unsubscribe so that we can reconnect with new arguments
							for (const key in this.replayQueries) {
								if (this.replayQueries[key]) {
									this.replayQueries[key].unsubscribe();
									delete this.replayQueries[key];
								}
							}
							this.client.terminate();
						}, KEEP_ALIVE / 2);
					}
				},
				pong: (received) => {
					if (received && this.timedOut !== null) {
						clearTimeout(this.timedOut);
					}
				},
			},
		});
	}

	private onConnected = (_socket: any, _payload: any, wasRetry: any) => {
		void getAnalyticsWebClient().then((client) => {
			client.sendOperationalEvent({
				source: 'confluence-RelayWebSocketSubscriptionHandler',
				actionSubject: 'webSocket',
				action: wasRetry ? 'reconnected' : 'connected',
			});
		});
	};

	private verifyAndUpdateInternetHealth = async () => {
		this.currentGetInternetConnectionStatePromise =
			this.currentGetInternetConnectionStatePromise || getInternetConnectionState();
		const internetHealthState = await this.currentGetInternetConnectionStatePromise;
		this.internetConnectionState = internetHealthState;
		this.currentGetInternetConnectionStatePromise = null;
	};

	private verifyAndUpdateInternetHealthUntilHealthy = async (): Promise<void> => {
		await this.verifyAndUpdateInternetHealth();

		if (this.internetConnectionState === 'healthy') {
			return;
		} else {
			this.client?.terminate();
		}

		await new Promise((resolve) => {
			setTimeout(resolve, 500);
		});
		return this.verifyAndUpdateInternetHealthUntilHealthy();
	};

	private retryWait = async (retries: number) => {
		const retryDelayRange = await getDelayRangeFromGraphqlWebsocketState(retries);
		// This is the logic reused from legacy WebSocket with a check verifyAndUpdateInternetHealthUntilHealthy
		// If the internet connection is healthy then we can retry, otherwise we do not need to retry
		// We subtract the time spent waiting to reconnect from the exponential backoff
		const startedAt = Date.now();
		await this.verifyAndUpdateInternetHealthUntilHealthy();
		// since we took some time to verify internet health, reduce the exponential backoff
		const msCheckingInternetHealth = Date.now() - startedAt;
		const retryDelay = getBackOffDelay({
			range: retryDelayRange,
			timeSpent: msCheckingInternetHealth,
		});

		// monitor status looks at if a user is idle or hidden to determine the retry delay
		await new Promise((resolve) => {
			let timeoutId = setTimeout(resolve, retryDelay);
			const newRetryDelayRange = calculateBackOffRange({
				status: 'active',
				attemptsCount: retries,
			});

			const msAlreadyWaited = Date.now() - startedAt;
			const newRetryDelay = getBackOffDelay({
				range: newRetryDelayRange,
				timeSpent: msAlreadyWaited,
			});

			const actualReconnectionDelay = Math.max(newRetryDelay - msAlreadyWaited, 0);
			// Clear old timeout and set a new one with the updated delay
			clearTimeout(timeoutId);
			timeoutId = setTimeout(resolve, actualReconnectionDelay);
		});
	};

	public subscribe(
		request: RequestParameters,
		variables: Variables,
		cacheConfig: CacheConfig,
		observer: {
			next: (value: any) => void;
			error: (error: Error) => void;
			complete: () => void;
		},
	): () => void {
		const errorCollector = startCaptureGraphQlErrors();
		// extensions is available on the variables object added by the useSubscriptionValues hook
		const baseExtensions =
			variables.__extensions || (cacheConfig?.metadata?.extensions as Record<string, any>);

		// Support persisted queries: when request.text is null but request.id exists,
		// send the persisted query ID via the 'pq' extension
		const extensions =
			request.id && !request.text ? { ...baseExtensions, pq: request.id } : baseExtensions;

		const subscription = this.client.subscribe<FormattedExecutionResult>(
			{
				query: request.text || '',
				variables,
				operationName: request.name,
				extensions,
			},
			{
				next: (result: FormattedExecutionResult) => {
					// Transform null data to empty object
					const transformedResult = {
						...result,
						data: result.data === null ? {} : result.data,
					};
					// if transformedResult has errors, capture them
					if (transformedResult.errors && transformedResult.errors.length > 0) {
						captureErrors(transformedResult.errors as AGGError[], {
							operationName: request.name,
							requestId: transformedResult.extensions?.gateway?.request_id,
						});
					}
					observer.next(transformedResult);
				},
				complete: () => {
					const errors = stopCaptureGraphQlErrors(errorCollector);
					if (errors.length > 0) {
						observer.error(
							new ApolloErrorInterop({ graphQLErrors: errors.flatMap((e) => e.errors) }),
						);
					} else {
						observer.complete();
					}
				},
				error: (err: any) => {
					if (this.closedInfo && !this.closedInfo?.wasClean && !err?.message) {
						void getAnalyticsWebClient().then((client) => {
							client.sendOperationalEvent({
								source: 'confluence-RelayWebSocketSubscriptionHandler',
								actionSubject: 'webSocket',
								action: 'closed-socket-error',
								attributes: {
									reason: this.closedInfo?.reason,
									code: this.closedInfo?.code,
									wasClean: this.closedInfo?.wasClean,
								},
							});
						});
					}
					if (err instanceof Error) {
						return observer.error(err);
					}
					return observer.error(new Error(`GraphQL WebSocket Error: ${JSON.stringify(err)}`));
				},
				// casting around a wrong type in graphql-ws, which incorrectly expects `Sink<ExecutionResult>`
			} satisfies Sink<FormattedExecutionResult> as any,
		);

		// keep track of the subscription so we can unsubscribe later
		const operationName = request.name || 'unknown';
		this.replayQueries[operationName] = {
			unsubscribe: subscription,
			query: request.text || '',
		};

		return subscription;
	}
}
